"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import ConfirmModal from "../ui/ConfirmModal";
import { SEND_BID_INITIAL_STATE } from "@/services/form.service";
import { CaseData, CMSData } from "@/cms/models";
import { submitBidForm } from "@/actions/send-bid-form";
import CurrencyInput from "../ui/inputs/CurrencyInput";
import TextInput from "../ui/inputs/TextInput";
import TextAreaInput from "../ui/inputs/TextAreaInput";
import FormLayout from "../ui/inputs/FormLayout";
import { useReCaptcha } from "next-recaptcha-v3";
import { ApplicationDataType } from "@/services/queries/get-application-details";

const BidForm: React.FC<{
  caseData: CaseData;
  appData: ApplicationDataType;
  cmsData: CMSData;
}> = ({ appData, cmsData, caseData }) => {
  const [selectedPrice, setSelectedPrice] = useState<string>("");
  const [randomString, setRandomString] = useState<string>("");
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    (prevState: any, formData: FormData) => {
      formData.append("caseId", caseData!.id!);
      return submitBidForm(prevState, formData).then((response) => {
        setSelectedPrice(response.resolvedPrice);
        setRandomString(Math.random().toString(36).substring(7));
        return response;
      });
    },
    SEND_BID_INITIAL_STATE
  );

  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", {
      enabled: formState.recaptchaIsEnabled,
      validToken: formState.recaptchaIsValid,
    });

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);

  const { executeRecaptcha } = useReCaptcha();
  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await executeRecaptcha("AssessmentForm");
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  const {
    priceValue,
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    message,
  } = formState?.data || {};

  return (
    <div className="">
      <ConfirmModal
        app={appData}
        title="Tak for dit bud"
        description="Har du spørgsmål kan du kontakte os på:"
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
      >
        <div className="flex flex-col">
          <div className="homepage-text-sm mb-3">
            <div>{`Du har givet et bud på  ${selectedPrice} på ${caseData!.displayName}`}</div>
          </div>
        </div>
      </ConfirmModal>
      <ConfirmModal
        app={null}
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <div className="flex flex-col gap-4 flex-1 w-full">
        <FormLayout
          submitLabel="Send bud"
          key={randomString}
          featureFlags={cmsData?.featureFlags}
          isPending={isPending}
          formAction={submit}
        >
          <CurrencyInput
            className="col-span-6"
            cmsData={cmsData}
            name="priceValue"
            label="Dit bud"
            defaultValue={priceValue}
            errors={formState?.zodErrors?.priceValue}
            required={true}
          />
          <TextInput
            cmsData={cmsData}
            name="firstName"
            label="Fornavn"
            defaultValue={firstName}
            errors={formState?.zodErrors?.firstName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            name="lastName"
            label="Efternavn"
            defaultValue={lastName}
            errors={formState?.zodErrors?.lastName}
            required={cmsData?.forms.nameRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-6"
            name="address"
            label="Adresse"
            defaultValue={address}
            errors={formState?.zodErrors?.address}
            required={cmsData?.forms.addressRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-2"
            name="zip"
            label="Postnr"
            defaultValue={zip}
            errors={formState?.zodErrors?.zip}
            required={cmsData?.forms.zipRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-4"
            name="city"
            label="By"
            defaultValue={city}
            errors={formState?.zodErrors?.city}
            required={cmsData?.forms.cityRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="phone"
            label="Telefonnummer"
            defaultValue={phone}
            errors={formState?.zodErrors?.phone}
            required={cmsData?.forms.phoneRequired}
          />
          <TextInput
            cmsData={cmsData}
            className="col-span-3"
            name="email"
            label="E-mail"
            defaultValue={email}
            errors={formState?.zodErrors?.email}
            required={cmsData?.forms.emailRequired}
          />
          <TextAreaInput
            cmsData={cmsData}
            name="message"
            label="Besked"
            defaultValue={message}
            errors={formState?.zodErrors?.message}
            required={false}
          />
        </FormLayout>
      </div>
    </div>
  );
};

export default BidForm;
